var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Paper, Stack } from '@mui/material';
import moment from 'moment';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { theme } from '../../config';
import { PlanType } from '../../enum';
import { formatDateToLocaleUppercase } from '../../util';
import { MULTISKILL_COLOR } from '../annual/constants';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '../common';
import { QueueType } from '../multiskill/types';
import { useReportAPI } from '../report/selectors';
import { SeatExceededIcon, useSeatCapacity } from '../seatCapacity';
import { DailyMetricHierarchyRows, FixedTableBanner, LEVEL_ROW_INDENTS, LEVEL_ROW_STICKIES, StyledDailyHeaderCell, StyledLevelFirstCell, updateUniqueIdSearchParam, } from './common';
import { CLOSED_INDICATOR } from './constants';
import { useDailyContext } from './context';
import { ContentNav, StyledAutocomplete, StyledJumpTo, TableNavBar } from './detailTable';
import { useDailySeatExceeded } from './seatExceeded';
import { isDailyQueueData, useDailyAPI, useDailyReportPlan, useDaysOfWeek, usePlanColor, useReportTimelineOptions, useVisibleMetrics, } from './selectors';
var getRowColor = function (rowKind, isTotalRow, bodyColor, isMultiskill) {
    if (isMultiskill === void 0) { isMultiskill = false; }
    if (isMultiskill)
        return MULTISKILL_COLOR;
    if (rowKind === 'location')
        return theme.palette.background.default;
    if (isTotalRow)
        return bodyColor;
    return undefined;
};
var formatIntervalHeaderTime = function (time) {
    var parsedTime = moment.utc(time, ['HH:mm', 'hh:mmA'], true);
    return parsedTime.isValid() ? parsedTime.format('hh:mmA') : time;
};
export var IntervalDetailTable = function () {
    var _a, _b, _c, _d;
    var i18n = useTranslation(['report', 'translation']).i18n;
    var _e = useDailyContext(), state = _e[0], setState = _e[1];
    var _f = useSearchParams(), searchParams = _f[0], setSearchParams = _f[1];
    var dailyData = useDailyAPI()[0];
    var reportData = useReportAPI()[0].data;
    var _g = usePlanColor(), headerColor = _g[0], bodyColor = _g[1];
    var dailyReportData = useDailyReportPlan();
    var timelines = useReportTimelineOptions();
    var visibleMetrics = useVisibleMetrics();
    var isIntervalExceeded = useDailySeatExceeded().isIntervalExceeded;
    var openAlertDetails = useSeatCapacity().openAlertDetails;
    var daysOfWeek = useDaysOfWeek();
    var activeDate = (_a = state === null || state === void 0 ? void 0 : state.activeDate) !== null && _a !== void 0 ? _a : (_b = daysOfWeek[0]) === null || _b === void 0 ? void 0 : _b.id;
    var dayIndex = useMemo(function () {
        if (!activeDate)
            return 0;
        var dateIndex = daysOfWeek.findIndex(function (day) { return day.id === activeDate; });
        return dateIndex >= 0 ? dateIndex : moment.utc(activeDate).isoWeekday() - 1;
    }, [activeDate, daysOfWeek]);
    var handlePrevDay = function () {
        if (!activeDate || !daysOfWeek.length)
            return;
        var idx = daysOfWeek.findIndex(function (d) { return d.id === activeDate; });
        if (idx > 0) {
            setState(function (curr) { return (curr ? __assign(__assign({}, curr), { activeDate: daysOfWeek[idx - 1].id }) : curr); });
        }
    };
    var handleNextDay = function () {
        if (!activeDate || !daysOfWeek.length)
            return;
        var idx = daysOfWeek.findIndex(function (d) { return d.id === activeDate; });
        if (idx >= 0 && idx < daysOfWeek.length - 1) {
            setState(function (curr) { return (curr ? __assign(__assign({}, curr), { activeDate: daysOfWeek[idx + 1].id }) : curr); });
        }
    };
    var handlePeriodChange = function (value) {
        if (value === undefined || value === null) {
            return;
        }
        var month = Number(value);
        var isMonthly = (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.Monthly || (reportData === null || reportData === void 0 ? void 0 : reportData.planType) === PlanType.MonthlyCustom;
        var periodType = isMonthly ? 'months' : 'weeks';
        var nextPeriodStart = (reportData === null || reportData === void 0 ? void 0 : reportData.startDate)
            ? moment.utc(reportData.startDate).add(month, periodType).format('YYYY-MM-DD')
            : undefined;
        setState(function (curr) {
            return curr
                ? __assign(__assign(__assign({}, curr), { month: month, startMonth: month, endMonth: month }), (nextPeriodStart ? { activeDate: nextPeriodStart } : {})) : curr;
        });
    };
    var data = dailyData.data;
    if (!isDailyQueueData(data))
        return null;
    var detail = data.detail;
    var countries = (_c = data.countries) !== null && _c !== void 0 ? _c : [];
    var queuesByLob = data.queuesByLob;
    var times = data.times || [];
    var selectedDay = daysOfWeek.find(function (d) { return d.id === activeDate; });
    var dayName = activeDate
        ? formatDateToLocaleUppercase(moment.utc(activeDate).locale(i18n.language).format('dddd'))
        : '';
    var currentDayLabel = (selectedDay === null || selectedDay === void 0 ? void 0 : selectedDay.name) || '';
    var renderValueCells = function (source, metric, rowKind) { return (_jsx(_Fragment, { children: times.map(function (time) {
            var _a, _b, _c;
            var value = (_c = (_b = (_a = source.detail) === null || _a === void 0 ? void 0 : _a[dayIndex]) === null || _b === void 0 ? void 0 : _b[time]) === null || _c === void 0 ? void 0 : _c[metric.idx];
            return (_jsx(TableCell, { value: value !== null && value !== void 0 ? value : CLOSED_INDICATOR, decimal: metric.decimal, percentage: metric.percentage, color: getRowColor(rowKind, source.detail === detail, bodyColor, source.queueType === QueueType.Multiskill || source.isMultiskillMember), align: "center" }, time));
        }) })); };
    var renderBlankCells = function (rowColor) { return (_jsx(_Fragment, { children: times.map(function (time) { return (_jsx(TableCell, { color: rowColor }, time)); }) })); };
    return (_jsxs(Paper, __assign({ elevation: 0, sx: { backgroundColor: theme.palette.background.default } }, { children: [_jsxs(ContentNav, __assign({ sx: { mb: 2 } }, { children: [_jsx(TableNavBar, { onNext: handleNextDay, onPrev: handlePrevDay, title: currentDayLabel, disabledNext: daysOfWeek.findIndex(function (d) { return d.id === activeDate; }) >= daysOfWeek.length - 1, disabledPrev: daysOfWeek.findIndex(function (d) { return d.id === activeDate; }) <= 0 }), _jsxs(StyledJumpTo, __assign({ sx: { gap: 2 } }, { children: [_jsx(StyledAutocomplete, { options: dailyReportData || [], getOptionValue: function (o) { return o.uniqueId; }, value: (state === null || state === void 0 ? void 0 : state.uniqueId) || '', onChange: function (val) {
                                    if (val) {
                                        setSearchParams(updateUniqueIdSearchParam(searchParams, val), { replace: true });
                                    }
                                }, disabled: !(dailyReportData === null || dailyReportData === void 0 ? void 0 : dailyReportData.length) }), _jsx(StyledAutocomplete, { options: timelines, value: (_d = state === null || state === void 0 ? void 0 : state.month) !== null && _d !== void 0 ? _d : '', onChange: handlePeriodChange, disabled: !timelines.length })] }))] })), _jsx(FixedTableBanner, { children: dayName }), _jsx(TableContainer, __assign({ maxHeight: "38rem" }, { children: _jsxs(Table, __assign({ sx: { tableLayout: 'auto' } }, { children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(StyledLevelFirstCell, { component: "td", greyColor: true, stickySizes: LEVEL_ROW_STICKIES, "aria-label": LEVEL_ROW_INDENTS.metric, sx: { width: LEVEL_ROW_STICKIES[0], backgroundColor: '#e4e4e4 !important' } }), times.map(function (time) { return (_jsx(StyledDailyHeaderCell, __assign({ greyColor: true, align: "center", sx: { color: headerColor } }, { children: _jsxs(Stack, __assign({ direction: "row", alignItems: "center", justifyContent: "center", spacing: 0.5 }, { children: [_jsx("span", { children: formatIntervalHeaderTime(time) }), isIntervalExceeded(dayIndex, time) && _jsx(SeatExceededIcon, { onClick: openAlertDetails })] })) }), time)); })] }) }), _jsx(TableBody, { children: _jsx(DailyMetricHierarchyRows, { visibleMetrics: visibleMetrics, detail: detail, countries: countries, queuesByLob: queuesByLob, bodyColor: bodyColor, renderValueCells: renderValueCells, renderBlankCells: renderBlankCells }) })] })) }))] })));
};
